import axios from 'axios';

import {
  BatchActionResult,
  MessageActionType,
  PlannerMessage
} from 'components/roster-portal/home/messages/types';
import { getEndOfDay, getStartOfDay } from 'utils/date';

const PLANNER_TASKS_URL = 'secure/users/{username}/tasks/all';
const TASK_SUBMIT_URL = 'secure/users/{username}/tasks/submitform';
const TASK_DEFINITIONS_URL = 'secure/users/{username}/tasks/definitions';
const LEAVE_REQUESTS_URL = 'secure/users/{username}/leave-requests';

export interface GetPlannerTasksParams {
  collection: string;
  taskDefinitionKeys?: string[];
  costCenterIds?: string[];
  startDate?: string;
  endDate?: string;
  range?: string;
}

export const getPlannerTasks = ({
  collection,
  taskDefinitionKeys,
  costCenterIds,
  startDate,
  endDate,
  range
}: GetPlannerTasksParams) => {
  const params: Record<string, string | string[]> = { collection };

  if (taskDefinitionKeys?.length) {
    params.taskDefinitionKey = taskDefinitionKeys;
  }
  if (costCenterIds?.length) {
    params.costCenters = costCenterIds.join(',');
  }
  if (startDate) {
    params.startDate = getStartOfDay(startDate).toISOString();
  }
  if (endDate) {
    params.endDate = getEndOfDay(endDate).toISOString();
  }

  return axios.get<PlannerMessage[]>(PLANNER_TASKS_URL, {
    params,
    paramsSerializer: (p) =>
      Object.entries(p)
        .flatMap(([key, value]) =>
          Array.isArray(value)
            ? value.map((v) => `${key}=${encodeURIComponent(v)}`)
            : [`${key}=${encodeURIComponent(value)}`]
        )
        .join('&'),
    headers: range ? { Range: range, 'X-Range': range } : {}
  });
};

export const getTaskDefinitions = () =>
  axios.get<Array<{ name: string; count?: number }>>(TASK_DEFINITIONS_URL, {
    params: { type: 'planner' }
  });

export const submitTaskAction = ({
  taskId,
  actionType,
  note
}: {
  taskId: string;
  actionType: MessageActionType;
  note?: string;
}) => {
  const formdata: Record<string, string> = { actions: actionType };
  if (note) {
    formdata.note = note;
  }
  return axios.post(`${TASK_SUBMIT_URL}/${taskId}`, { formdata });
};

export const submitBatchTaskAction = ({
  taskIds,
  actionType,
  note
}: {
  taskIds: string[];
  actionType: MessageActionType;
  note?: string;
}) =>
  axios.put<BatchActionResult>(`${TASK_SUBMIT_URL}/batch`, {
    taskIds,
    formData: { actions: actionType, ...(note && { note }) }
  });

export const deletePlannerTask = (processInstanceId: string) =>
  axios.delete(`${PLANNER_TASKS_URL}/${processInstanceId}`);

export const rejectLeaveRequest = ({
  leaveRequestId,
  note
}: {
  leaveRequestId: string;
  note: string;
}) =>
  axios.put(`${LEAVE_REQUESTS_URL}/${leaveRequestId}/task`, {
    plannerNote: note
  });
